import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  BanknotesIcon,
  BeakerIcon,
  BoltIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  CircleStackIcon,
  ClipboardDocumentListIcon,
  CloudIcon,
  CodeBracketIcon,
  CogIcon,
  CpuChipIcon,
  CreditCardIcon,
  CubeIcon,
  DocumentChartBarIcon,
  DocumentCheckIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  IdentificationIcon,
  InboxStackIcon,
  KeyIcon,
  LinkIcon,
  MapIcon,
  MapPinIcon,
  PresentationChartLineIcon,
  PuzzlePieceIcon,
  QueueListIcon,
  ReceiptPercentIcon,
  ServerStackIcon,
  ShieldCheckIcon,
  ShoppingBagIcon,
  ShoppingCartIcon,
  SparklesIcon,
  SquaresPlusIcon,
  TagIcon,
  TruckIcon,
  UserGroupIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";

import useNotification from "../../components/notifications/useNotifications";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import FileUpload from "../../components/shared/Inputs/FileUpload";
import { NumberInput } from "../../components/shared/Inputs/NumberInput";
import { TextInput } from "../../components/shared/Inputs/TextInput";
import LoadingWheel from "../../components/shared/LoadingWheel";
import useCustomerDetails from "../../data/useCustomerDetails";
import useOrderGenerator from "../../data/useOrderGenerator";
import classNames from "../../utils/classNames";

interface IModule {
  id: string;
  name: string;
  description: string;
  icon: typeof CogIcon;
  recommended?: boolean;
}

interface IModuleGroup {
  id: string;
  name: string;
  blurb: string;
  modules: IModule[];
}

const MODULE_GROUPS: IModuleGroup[] = [
  {
    id: "core",
    name: "Core Operations",
    blurb: "End-to-end lead → vehicle → part workflows that drive your yard every day.",
    modules: [
      { id: "leads", name: "Lead & Quote Management", description: "Capture, score and convert vehicle purchase leads with built-in quoting and reminders.", icon: ClipboardDocumentListIcon, recommended: true },
      { id: "vehicleIntake", name: "Vehicle Intake & Awaiting Arrival", description: "Track every vehicle from quote acceptance through collection to arrival on site.", icon: TruckIcon, recommended: true },
      { id: "depollution", name: "Depollution & Compliance", description: "ATF-grade depollution checklists, fluid tracking and audit-ready reporting.", icon: BeakerIcon },
      { id: "jobs", name: "Dismantling Workflow / Jobs", description: "Assign vehicles to dismantlers, capture parts, photos and time per job.", icon: WrenchScrewdriverIcon, recommended: true },
      { id: "stock", name: "Stock & Inventory", description: "Live multi-warehouse stock with linkage back to source vehicles and lookups.", icon: CubeIcon, recommended: true },
      { id: "stockQc", name: "Parts Quality Control", description: "Structured QC pass before parts reach the marketplace — required photos, tests, defects.", icon: CheckBadgeIcon },
      { id: "core", name: "Core / Returns Management", description: "Track core returns, refurb routing and exchange units across customers.", icon: ArrowPathIcon },
      { id: "workOrders", name: "Work Orders", description: "Internal jobs against vehicles or parts — repairs, valeting, repackaging.", icon: ClipboardDocumentListIcon },
      { id: "production", name: "Production Manager", description: "Plan dismantling capacity, throughput targets and team productivity.", icon: PresentationChartLineIcon },
      { id: "racking", name: "Vehicle Racking", description: "Physical racking and warehouse-location management for parts.", icon: SquaresPlusIcon },
    ],
  },
  {
    id: "marketplaces",
    name: "Sales & Marketplaces",
    blurb: "Push your inventory to where the buyers are, with automation built in.",
    modules: [
      { id: "ebayListings", name: "eBay Listings", description: "List, manage and bulk-edit thousands of eBay listings from one screen.", icon: ShoppingBagIcon, recommended: true },
      { id: "ebayOrders", name: "eBay Orders", description: "Pull orders, print labels, track fulfilment and reconcile against stock.", icon: ShoppingCartIcon, recommended: true },
      { id: "ebayReturns", name: "eBay Returns", description: "Manage cases, refunds and return shipping in line with eBay policy.", icon: ArrowPathIcon },
      { id: "ebayMessages", name: "eBay Messages", description: "Unified inbox for buyer messages with templates and SLA tracking.", icon: ChatBubbleLeftRightIcon },
      { id: "marketplaceInsights", name: "Marketplace Insights", description: "Pricing benchmarks, category performance and competitor analysis.", icon: ChartBarIcon },
      { id: "bidpro", name: "BidPro Auctions", description: "Bid on salvage auctions with profit modelling baked in.", icon: TagIcon },
      { id: "bulkList", name: "Bulk Listing Tools", description: "Batch list, batch edit and batch end large volumes of stock.", icon: QueueListIcon },
      { id: "shopify", name: "Shopify Integration", description: "Sync stock, prices and orders to a Shopify storefront.", icon: ShoppingCartIcon },
    ],
  },
  {
    id: "logistics",
    name: "Logistics & Customer",
    blurb: "Move metal, talk to customers and keep drivers on schedule.",
    modules: [
      { id: "calendar", name: "Calendar & Trip Manager", description: "Build collection trips, optimise routes and assign to drivers.", icon: CalendarDaysIcon, recommended: true },
      { id: "driverApp", name: "Driver Tracking & App", description: "Live driver location, on-site checklists and signature capture.", icon: MapPinIcon },
      { id: "dropOff", name: "Drop-Off Scheduling", description: "Customer drop-off slot booking with automated confirmations.", icon: InboxStackIcon },
      { id: "contacts", name: "Customer & Contact CRM", description: "Single view of customers, sellers, trade buyers and breakers.", icon: UserGroupIcon, recommended: true },
      { id: "accounts", name: "Accounts", description: "Trade accounts, credit limits, statements and per-account pricing.", icon: IdentificationIcon },
      { id: "chat", name: "Chat / Messaging", description: "Real-time internal chat between staff, drivers and dismantlers.", icon: ChatBubbleLeftRightIcon },
      { id: "widget", name: "Web Quote Widget", description: "Embed a scrap/sell-a-vehicle quote widget on your website.", icon: GlobeAltIcon },
    ],
  },
  {
    id: "finance",
    name: "Finance & Reporting",
    blurb: "Get paid faster and report on every angle of the business.",
    modules: [
      { id: "invoicing", name: "Invoicing", description: "Customer and supplier invoices generated from jobs and orders.", icon: DocumentTextIcon, recommended: true },
      { id: "payments", name: "Payments (Stripe)", description: "Take card payments online or in person, fully reconciled.", icon: CreditCardIcon },
      { id: "telleroo", name: "Telleroo Banking", description: "Push driver payouts straight to bank via Telleroo.", icon: BanknotesIcon },
      { id: "accounting", name: "Accounting Sync", description: "Two-way sync with Xero, Sage, QuickBooks or your accounting stack.", icon: ReceiptPercentIcon },
      { id: "reports", name: "Reporting & Analytics", description: "Pre-built reports for vehicles, parts, salesman, dismantler and inventory.", icon: DocumentChartBarIcon, recommended: true },
      { id: "dashboards", name: "Custom Dashboards", description: "Bespoke KPI dashboards for owners, managers and ops leads.", icon: ChartBarIcon },
    ],
  },
  {
    id: "platform",
    name: "Platform & Integrations",
    blurb: "Enterprise-grade infrastructure that plugs into the rest of your stack.",
    modules: [
      { id: "api", name: "REST API Access", description: "Full API access for your own apps, partners and connectors.", icon: CodeBracketIcon },
      { id: "connectors", name: "Custom Connectors", description: "Bespoke integrations to legacy DMS, ERP or proprietary systems.", icon: PuzzlePieceIcon },
      { id: "warehouse", name: "Data Warehouse Pipeline", description: "Stream operational data into your warehouse (Snowflake, BigQuery, etc).", icon: CircleStackIcon },
      { id: "vehicleData", name: "Vehicle Data Feeds", description: "Inbound feeds for VRM lookup, parts compatibility and OEM data.", icon: CpuChipIcon },
      { id: "maxoptra", name: "Maxoptra Routing", description: "Plug into Maxoptra for multi-vehicle route optimisation.", icon: MapIcon },
      { id: "sso", name: "Single Sign-On (SSO)", description: "SAML / OIDC SSO with your existing identity provider.", icon: KeyIcon },
      { id: "multiSite", name: "Multi-Site / Multi-Warehouse", description: "Run multiple sites and warehouses with isolated stock and pricing.", icon: BuildingOffice2Icon },
      { id: "ha", name: "High Availability Hosting", description: "Multi-region, geo-redundant deployment with 99.9% SLA.", icon: ServerStackIcon },
    ],
  },
];

const HOSTING_OPTIONS = [
  { id: "cloud", label: "Cloud (managed by Wilma)", icon: CloudIcon },
  { id: "hybrid", label: "Hybrid", icon: PuzzlePieceIcon },
  { id: "ha", label: "High Availability", icon: ServerStackIcon },
  { id: "onprem", label: "On-Premise", icon: ServerStackIcon },
];

const PRICING_OPTIONS = [
  { id: "subscription", label: "Subscription", icon: ArrowPathIcon },
  { id: "usage", label: "Usage-Based", icon: BoltIcon },
  { id: "hybrid", label: "Hybrid", icon: PuzzlePieceIcon },
  { id: "unsure", label: "Not sure yet", icon: SparklesIcon },
];

const NAV_SECTIONS = [
  { id: "company", label: "About You" },
  { id: "scale", label: "Scale & Operations" },
  { id: "inventory", label: "Inventory & Data" },
  { id: "migration", label: "Data Migration" },
  { id: "modules", label: "Features & Modules" },
  { id: "integrations", label: "Integrations" },
  { id: "api", label: "API & Reporting" },
  { id: "infrastructure", label: "Infrastructure" },
  { id: "workflow", label: "Workflow Needs" },
  { id: "commercials", label: "Pricing" },
  { id: "documents", label: "Documents" },
  { id: "additional", label: "Additional Notes" },
];

const HERO_STATS = [
  { value: "Multi-site", label: "Run unlimited sites & warehouses" },
  { value: "API-first", label: "Plug into anything you already run" },
  { value: "Real-time", label: "Live stock, listings, drivers & jobs" },
  { value: "Tailored", label: "Modules & workflows shaped to you" },
];

export default function OrderGenerator() {
  const { register, handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      hostingModel: "cloud",
      pricingModel: "subscription",
    } as Record<string, any>,
  });

  const { addNotification } = useNotification();
  const { submit } = useOrderGenerator();
  const { publicBrand: brand } = useCustomerDetails();

  const [selectedModules, setSelectedModules] = useState<Set<string>>(
    () => new Set(MODULE_GROUPS.flatMap((g) => g.modules.filter((m) => m.recommended).map((m) => m.id)))
  );
  const [documents, setDocuments] = useState<File[]>([]);
  const [isFinished, setIsFinished] = useState(false);

  const totalModules = useMemo(
    () => MODULE_GROUPS.reduce((acc, g) => acc + g.modules.length, 0),
    []
  );

  const toggleModule = (id: string) => {
    setSelectedModules((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onSubmit = (data: Record<string, any>) => {
    if (!data.contactName || !data.email || !data.businessName) {
      addNotification({
        variant: "error",
        primaryText: "Please fill in your name, business and email so we can get back to you.",
      });
      return;
    }

    const formData = new FormData();
    formData.append(
      "payload",
      JSON.stringify({
        ...data,
        selectedModules: Array.from(selectedModules),
      })
    );

    documents.forEach((file, idx) => {
      formData.append(`document_${idx}`, file);
    });

    submit.mutate(
      { emailId: data.email, data: formData },
      {
        onSuccess: () => {
          setIsFinished(true);
          window.scrollTo({ top: 0, behavior: "smooth" });
        },
        onError: () => {
          addNotification({
            variant: "error",
            primaryText: "We couldn't submit your requirements. Please try again or email us directly.",
          });
        },
      }
    );
  };

  if (isFinished) {
    return <ThankYouScreen logo={brand.data?.data.logo} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50">
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-white/80 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {brand.data?.data.logo ? (
              <img src={brand.data.data.logo} alt="Logo" className="h-9 w-auto" />
            ) : (
              <div className="font-semibold text-lg tracking-tight">Wilma</div>
            )}
            <span className="hidden sm:inline-block text-gray-300">|</span>
            <span className="hidden sm:inline-block text-sm font-medium text-gray-600">
              Requirements & Discovery
            </span>
          </div>
          <a
            href="#company"
            className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            Start the form
            <ArrowDownTrayIcon className="h-4 w-4 rotate-180" />
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900" aria-hidden="true" />
        <div className="absolute inset-0 opacity-30 [background-image:radial-gradient(circle_at_30%_20%,white,transparent_45%),radial-gradient(circle_at_75%_70%,white,transparent_45%)]" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-white">
          <div className="max-w-3xl">
            <p className="inline-flex items-center gap-2 text-sm font-medium uppercase tracking-wide bg-white/15 px-3 py-1 rounded-full backdrop-blur">
              <SparklesIcon className="h-4 w-4" />
              Build your platform
            </p>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
              Tell us about your recycling operation.
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-white/85 leading-relaxed">
              We'll use what you share below to scope your implementation, choose the right
              modules, and put together a tailored commercial proposal. The more detail you give
              us, the sharper the proposal we send back.
            </p>

            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
              {HERO_STATS.map((s) => (
                <div key={s.label} className="bg-white/10 backdrop-blur rounded-lg p-4 border border-white/15">
                  <div className="text-xl font-semibold">{s.value}</div>
                  <div className="text-sm text-white/75 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Form body */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Sidebar nav */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-24">
              <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
                  Sections
                </p>
                <nav className="flex flex-col gap-1">
                  {NAV_SECTIONS.map((s, idx) => (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      className="group flex items-center gap-3 text-sm text-gray-700 hover:text-primary-600 px-2 py-1.5 rounded-md hover:bg-primary-50"
                    >
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-[11px] font-semibold text-gray-600 group-hover:bg-primary-600 group-hover:text-white">
                        {idx + 1}
                      </span>
                      <span>{s.label}</span>
                    </a>
                  ))}
                </nav>
              </div>

              <div className="mt-4 bg-primary-50 border border-primary-200 rounded-lg p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                  Modules selected
                </p>
                <p className="mt-1 text-2xl font-bold text-primary-900">
                  {selectedModules.size}
                  <span className="text-base font-medium text-primary-700"> / {totalModules}</span>
                </p>
                <p className="mt-2 text-xs text-primary-800/80 leading-relaxed">
                  Pick everything you'd like to use. We'll suggest a starter set, but mix &amp; match
                  freely — your final implementation will be tailored.
                </p>
              </div>
            </div>
          </aside>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="lg:col-span-9 space-y-12">
            {/* About you */}
            <FormSection
              id="company"
              step={1}
              title="About you"
              description="Who are we talking to, and where can we send the proposal?"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextInput label="Your name" register={register} registerName="contactName" required cols="" rounded />
                <TextInput label="Job title" register={register} registerName="contactRole" cols="" rounded />
                <TextInput label="Business name" register={register} registerName="businessName" required cols="" rounded />
                <TextInput label="Website" register={register} registerName="website" placeholder="https://" cols="" rounded />
                <TextInput label="Email" register={register} registerName="email" type="email" required cols="" rounded />
                <TextInput label="Phone" register={register} registerName="phone" cols="" rounded />
                <TextInput label="Country" register={register} registerName="country" cols="" rounded />
                <TextInput label="Existing software (if any)" register={register} registerName="existingSoftware" cols="" rounded />
              </div>
            </FormSection>

            {/* Scale */}
            <FormSection
              id="scale"
              step={2}
              title="Business scale &amp; operations"
              description="Helps us size the platform — multi-site capacity, user counts and throughput."
            >
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <NumberInput label="Number of sites" register={register} registerName="numSites" step="1" min={0} />
                <NumberInput label="Warehouses / locations" register={register} registerName="numWarehouses" step="1" min={0} />
                <NumberInput label="Total users (est.)" register={register} registerName="numUsersTotal" step="1" min={0} />
                <NumberInput label="Admin users" register={register} registerName="numAdminUsers" step="1" min={0} />
                <NumberInput label="Warehouse users" register={register} registerName="numWarehouseUsers" step="1" min={0} />
                <NumberInput label="Dismantling users" register={register} registerName="numDismantlingUsers" step="1" min={0} />
                <NumberInput label="Customer accounts" register={register} registerName="numCustomerAccounts" step="1" min={0} />
                <NumberInput label="Vehicles processed / year" register={register} registerName="vehiclesPerYear" step="1" min={0} />
                <NumberInput label="Parts processed / year" register={register} registerName="partsPerYear" step="1" min={0} />
              </div>
            </FormSection>

            {/* Inventory */}
            <FormSection
              id="inventory"
              step={3}
              title="Inventory &amp; data volumes"
              description="A rough sense of the data we'll be carrying for you on day one."
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <NumberInput label="Parts in stock today" register={register} registerName="currentPartsInStock" step="1" min={0} />
                <NumberInput label="Vehicles linked to stock" register={register} registerName="vehiclesLinkedToStock" step="1" min={0} />
                <NumberInput label="Vehicle data lookups / month" register={register} registerName="vehicleDataLookups" step="1" min={0} />
              </div>
            </FormSection>

            {/* Migration */}
            <FormSection
              id="migration"
              step={4}
              title="Data migration"
              description="Tell us what's coming with you. We've migrated from spreadsheets, legacy DMSs and bespoke databases."
            >
              <div className="grid grid-cols-1 gap-4">
                <TextInput
                  label="Data sources (DMS, spreadsheets, databases…)"
                  register={register}
                  registerName="dataSources"
                  richText
                  rows={2}
                  cols=""
                  rounded
                />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <NumberInput label="Parts to migrate" register={register} registerName="partsToMigrate" step="1" min={0} />
                  <NumberInput label="Vehicle records to migrate" register={register} registerName="vehiclesToMigrate" step="1" min={0} />
                  <NumberInput label="Months of sold history" register={register} registerName="monthsOfSoldHistory" step="1" min={0} />
                </div>
                <TextInput
                  label="Known data quality issues"
                  register={register}
                  registerName="dataQualityNotes"
                  richText
                  rows={3}
                  cols=""
                  rounded
                />
              </div>
            </FormSection>

            {/* Modules */}
            <FormSection
              id="modules"
              step={5}
              title="Features &amp; modules"
              description="Pick the modules you want included. We've pre-selected a typical starter set — adjust freely."
            >
              <div className="space-y-10">
                {MODULE_GROUPS.map((group) => (
                  <div key={group.id}>
                    <div className="flex items-baseline justify-between mb-4">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">{group.name}</h4>
                        <p className="text-sm text-gray-500 mt-0.5">{group.blurb}</p>
                      </div>
                      <span className="text-xs font-medium text-gray-500 whitespace-nowrap">
                        {group.modules.filter((m) => selectedModules.has(m.id)).length}/{group.modules.length}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                      {group.modules.map((m) => (
                        <ModuleCard
                          key={m.id}
                          module={m}
                          selected={selectedModules.has(m.id)}
                          onToggle={() => toggleModule(m.id)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </FormSection>

            {/* Integrations */}
            <FormSection
              id="integrations"
              step={6}
              title="Integrations &amp; connectivity"
              description="Tell us what we need to talk to — accounting, routing, vehicle data, custom connectors."
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextInput
                  label="Accounting software"
                  register={register}
                  registerName="accountingSoftware"
                  placeholder="e.g. Xero, Sage, QuickBooks"
                  cols=""
                  rounded
                />
                <TextInput
                  label="Vehicle data provider(s)"
                  register={register}
                  registerName="vehicleDataProvider"
                  placeholder="e.g. UKVD, HPI, OEM feed"
                  cols=""
                  rounded
                />
              </div>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <ToggleCard
                  label="Maxoptra (routing/logistics)"
                  description="Hand collection trips off to Maxoptra for route optimisation."
                  registerName="needsMaxoptra"
                  watch={watch}
                  setValue={setValue}
                  register={register}
                  icon={MapIcon}
                />
                <ToggleCard
                  label="Data warehouse connection"
                  description="Stream operational data into Snowflake / BigQuery / Redshift."
                  registerName="needsDataWarehouse"
                  watch={watch}
                  setValue={setValue}
                  register={register}
                  icon={CircleStackIcon}
                />
                <ToggleCard
                  label="Vehicle data feeds via API"
                  description="Inbound feeds for VRM lookup, parts compatibility, OEM data."
                  registerName="needsVehicleFeeds"
                  watch={watch}
                  setValue={setValue}
                  register={register}
                  icon={CpuChipIcon}
                />
                <ToggleCard
                  label="Custom API / connectors required"
                  description="Bespoke connectors to a system not on our standard list."
                  registerName="needsCustomConnectors"
                  watch={watch}
                  setValue={setValue}
                  register={register}
                  icon={PuzzlePieceIcon}
                />
              </div>
              <div className="mt-6">
                <TextInput
                  label="Integration purpose & data direction (per integration)"
                  register={register}
                  registerName="integrationsNotes"
                  richText
                  rows={4}
                  placeholder={"e.g. Xero — invoices, outbound\\nUKVD — VRM lookup, inbound"}
                  cols=""
                  rounded
                />
              </div>
            </FormSection>

            {/* API & Reporting */}
            <FormSection
              id="api"
              step={7}
              title="API, data &amp; reporting"
              description="What does your team need to build, query and report on?"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <ToggleCard label="API access required" description="Full REST API access for your apps and partners." registerName="apiAccess" watch={watch} setValue={setValue} register={register} icon={CodeBracketIcon} />
                <ToggleCard label="Custom connectors required" description="Connectors to legacy or proprietary systems." registerName="customConnectors" watch={watch} setValue={setValue} register={register} icon={LinkIcon} />
                <ToggleCard label="Data warehouse integration" description="Feed our data into your warehouse for BI." registerName="warehouseIntegration" watch={watch} setValue={setValue} register={register} icon={CircleStackIcon} />
                <ToggleCard label="Custom dashboards required" description="Bespoke dashboards beyond our standard reports." registerName="customDashboards" watch={watch} setValue={setValue} register={register} icon={ChartBarIcon} />
              </div>
              <div className="mt-6">
                <TextInput
                  label="Custom reporting requirements"
                  register={register}
                  registerName="customReporting"
                  richText
                  rows={3}
                  cols=""
                  rounded
                />
              </div>
            </FormSection>

            {/* Infrastructure */}
            <FormSection
              id="infrastructure"
              step={8}
              title="Infrastructure &amp; IT"
              description="We support cloud, hybrid and high-availability deployments. Tell us what you need."
            >
              <div>
                <label className="text-sm font-medium text-gray-500 mb-2 block">Preferred hosting model</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {HOSTING_OPTIONS.map((opt) => (
                    <ChoiceCard
                      key={opt.id}
                      label={opt.label}
                      icon={opt.icon}
                      selected={watch("hostingModel") === opt.id}
                      onClick={() => setValue("hostingModel", opt.id)}
                    />
                  ))}
                </div>
              </div>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <ToggleCard label="Single Sign-On (SSO)" description="SAML / OIDC against your identity provider." registerName="needsSso" watch={watch} setValue={setValue} register={register} icon={KeyIcon} />
                <ToggleCard label="Geo redundancy" description="Multi-region failover for disaster recovery." registerName="needsGeoRedundancy" watch={watch} setValue={setValue} register={register} icon={GlobeAltIcon} />
              </div>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextInput label="Backup expectations" register={register} registerName="backupExpectations" richText rows={2} cols="" rounded />
                <TextInput label="Security requirements" register={register} registerName="securityRequirements" richText rows={2} cols="" rounded />
                <TextInput label="Compliance requirements" register={register} registerName="complianceRequirements" richText rows={2} cols="" rounded />
                <TextInput label="Network / on-prem constraints" register={register} registerName="networkConstraints" richText rows={2} cols="" rounded />
              </div>
            </FormSection>

            {/* Workflow */}
            <FormSection
              id="workflow"
              step={9}
              title="Workflow needs"
              description="Anything specific to how you run vehicles and parts that we should know about."
            >
              <div className="grid grid-cols-1 gap-4">
                <TextInput label="Custom vehicle workflows" register={register} registerName="customVehicleWorkflows" richText rows={2} cols="" rounded />
                <TextInput label="Custom parts workflows" register={register} registerName="customPartsWorkflows" richText rows={2} cols="" rounded />
                <TextInput label="How are non-saleable parts handled today?" register={register} registerName="nonSaleableParts" richText rows={2} cols="" rounded />
                <TextInput label="Custom data fields required" register={register} registerName="customFields" richText rows={2} cols="" rounded />
                <TextInput label="Operational constraints" register={register} registerName="operationalConstraints" richText rows={2} cols="" rounded />
              </div>
            </FormSection>

            {/* Pricing */}
            <FormSection
              id="commercials"
              step={10}
              title="Pricing &amp; commercials"
              description="Helps us send back something realistic — your team's preference and rough budget."
            >
              <div>
                <label className="text-sm font-medium text-gray-500 mb-2 block">Preferred pricing model</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {PRICING_OPTIONS.map((opt) => (
                    <ChoiceCard
                      key={opt.id}
                      label={opt.label}
                      icon={opt.icon}
                      selected={watch("pricingModel") === opt.id}
                      onClick={() => setValue("pricingModel", opt.id)}
                    />
                  ))}
                </div>
              </div>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextInput label="Indicative budget range" register={register} registerName="budgetRange" placeholder="£" cols="" rounded />
                <TextInput label="Target go-live date" register={register} registerName="targetGoLive" placeholder="e.g. Q3 2026" cols="" rounded />
              </div>
            </FormSection>

            {/* Documents */}
            <FormSection
              id="documents"
              step={11}
              title="Supporting documents"
              description="Optional — drop anything that helps us scope (RFP, current data exports, ER diagrams, screenshots)."
            >
              <FileUpload
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg"
                label="Attach documents"
                desc="PDF, Word, Excel, CSV or images — up to 10 files"
                multiple
                updateFilesCb={(files: File[]) => setDocuments(files)}
              />
            </FormSection>

            {/* Additional */}
            <FormSection
              id="additional"
              step={12}
              title="Anything else?"
              description="Constraints, risks, things you've struggled with on other platforms — anything goes here."
            >
              <TextInput
                label=""
                register={register}
                registerName="additionalNotes"
                richText
                rows={5}
                cols=""
                rounded
              />
            </FormSection>

            {/* Submit */}
            <div className="border-t border-gray-200 pt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <CheckCircleIcon className="h-5 w-5 text-primary-600" />
                <span>
                  You've selected <strong className="text-gray-900">{selectedModules.size}</strong> module
                  {selectedModules.size === 1 ? "" : "s"} and attached{" "}
                  <strong className="text-gray-900">{documents.length}</strong> document
                  {documents.length === 1 ? "" : "s"}.
                </span>
              </div>
              <ButtonInput
                label={submit.isLoading ? "Submitting…" : "Submit my requirements"}
                isSubmit
                isLoading={submit.isLoading}
                onClick={null}
                size="lg"
                classes="rounded-md"
              />
            </div>
          </form>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <ShieldCheckIcon className="h-4 w-4" />
            <span>Your information is only used to build your proposal — never shared.</span>
          </div>
          <div className="flex items-center gap-2">
            <EnvelopeIcon className="h-4 w-4" />
            <span>Questions? Reply to the email this form was sent in.</span>
          </div>
        </div>
      </footer>

      {submit.isLoading && (
        <div className="fixed inset-0 z-40 bg-black/30 flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 shadow-xl flex flex-col items-center gap-4 max-w-sm text-center">
            <LoadingWheel />
            <p className="text-sm font-medium text-gray-700">
              Sending your requirements — please don't close this window.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

interface FormSectionProps {
  id: string;
  step: number;
  title: string;
  description: string;
  children: React.ReactNode;
}

function FormSection({ id, step, title, description, children }: FormSectionProps) {
  return (
    <section id={id} className="scroll-mt-24">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 sm:p-8">
        <div className="flex items-start gap-4 mb-6">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700 text-sm font-semibold">
            {step}
          </span>
          <div>
            <h3
              className="text-xl font-semibold text-gray-900"
              dangerouslySetInnerHTML={{ __html: title }}
            />
            <p className="mt-1 text-sm text-gray-500">{description}</p>
          </div>
        </div>
        {children}
      </div>
    </section>
  );
}

interface ModuleCardProps {
  module: IModule;
  selected: boolean;
  onToggle: () => void;
}

function ModuleCard({ module, selected, onToggle }: ModuleCardProps) {
  const Icon = module.icon;
  return (
    <button
      type="button"
      onClick={onToggle}
      className={classNames(
        "group relative text-left rounded-lg border p-4 transition-all",
        selected
          ? "border-primary-500 bg-primary-50/60 shadow-sm ring-1 ring-primary-500"
          : "border-gray-200 bg-white hover:border-primary-300 hover:bg-primary-50/30"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span
            className={classNames(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-md",
              selected ? "bg-primary-600 text-white" : "bg-gray-100 text-gray-600 group-hover:bg-primary-100 group-hover:text-primary-700"
            )}
          >
            <Icon className="h-5 w-5" />
          </span>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">{module.name}</span>
              {module.recommended && !selected && (
                <span className="text-[10px] font-medium uppercase tracking-wide text-primary-700 bg-primary-100 px-1.5 py-0.5 rounded">
                  Recommended
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500 leading-snug">{module.description}</p>
          </div>
        </div>
        <span
          className={classNames(
            "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition",
            selected ? "border-primary-600 bg-primary-600 text-white" : "border-gray-300 bg-white"
          )}
        >
          {selected && <CheckCircleIcon className="h-5 w-5" />}
        </span>
      </div>
    </button>
  );
}

interface ToggleCardProps {
  label: string;
  description: string;
  registerName: string;
  register: any;
  watch: any;
  setValue: any;
  icon: typeof CogIcon;
}

function ToggleCard({ label, description, registerName, register, watch, setValue, icon: Icon }: ToggleCardProps) {
  const checked = !!watch(registerName);
  return (
    <button
      type="button"
      onClick={() => setValue(registerName, !checked)}
      className={classNames(
        "text-left rounded-lg border p-4 transition-all flex items-start gap-3",
        checked
          ? "border-primary-500 bg-primary-50/60 shadow-sm"
          : "border-gray-200 bg-white hover:border-primary-300 hover:bg-primary-50/30"
      )}
    >
      <span
        className={classNames(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-md",
          checked ? "bg-primary-600 text-white" : "bg-gray-100 text-gray-600"
        )}
      >
        <Icon className="h-5 w-5" />
      </span>
      <div className="flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-gray-900">{label}</span>
          <span
            className={classNames(
              "relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition",
              checked ? "bg-primary-600" : "bg-gray-200"
            )}
            aria-hidden="true"
          >
            <span
              className={classNames(
                "inline-block h-4 w-4 rounded-full bg-white shadow transform transition",
                checked ? "translate-x-4" : "translate-x-0"
              )}
            />
          </span>
        </div>
        <p className="mt-1 text-xs text-gray-500 leading-snug">{description}</p>
      </div>
      {/* Hidden input keeps RHF in sync */}
      <input type="checkbox" className="hidden" {...register(registerName)} checked={checked} readOnly />
    </button>
  );
}

interface ChoiceCardProps {
  label: string;
  icon: typeof CogIcon;
  selected: boolean;
  onClick: () => void;
}

function ChoiceCard({ label, icon: Icon, selected, onClick }: ChoiceCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(
        "rounded-lg border p-4 flex flex-col items-center gap-2 text-center transition-all",
        selected
          ? "border-primary-500 bg-primary-50 shadow-sm ring-1 ring-primary-500"
          : "border-gray-200 bg-white hover:border-primary-300 hover:bg-primary-50/30"
      )}
    >
      <span
        className={classNames(
          "flex h-9 w-9 items-center justify-center rounded-md",
          selected ? "bg-primary-600 text-white" : "bg-gray-100 text-gray-600"
        )}
      >
        <Icon className="h-5 w-5" />
      </span>
      <span className="text-sm font-medium text-gray-900">{label}</span>
    </button>
  );
}

function ThankYouScreen({ logo }: { logo?: string }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-50 flex items-center justify-center px-4">
      <div className="max-w-xl w-full bg-white rounded-2xl border border-gray-200 shadow-lg p-10 text-center">
        {logo ? (
          <img src={logo} alt="Logo" className="mx-auto h-12 w-auto mb-6" />
        ) : (
          <div className="mx-auto h-12 mb-6 font-bold text-xl">Wilma</div>
        )}
        <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-700 mb-6">
          <DocumentCheckIcon className="h-7 w-7" />
        </span>
        <h1 className="text-2xl font-bold text-gray-900">Thanks — we've got it.</h1>
        <p className="mt-3 text-gray-600 leading-relaxed">
          Your requirements have been sent to our team. We'll review them and come back with a
          tailored proposal, scope and commercials within a few working days.
        </p>
        <p className="mt-6 text-xs text-gray-500">
          You can close this window — no further action needed.
        </p>
      </div>
    </div>
  );
}
